import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faPlus,
  faTrashCan,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { faqEntryIssues } from './faq';

/** First language that has a question – the label of a collapsed entry. */
function previewOf(entry, languages) {
  const code = languages.find((language) => entry.question[language]?.trim());
  return code ? entry.question[code] : '';
}

const FaqEditor = ({
  questions,
  languages,
  className,
  onChangeField,
  onAddEntry,
  onRemoveEntry,
  onMoveEntry,
}) => {
  const [expanded, setExpanded] = useState({});

  const toggle = (index) =>
    setExpanded((current) => {
      const next = { ...current };
      if (next[index]) delete next[index];
      else next[index] = true;
      return next;
    });

  return (
    <div className="TranslationEditor-faq">
      <div className="TranslationEditor-headRow faq">
        <span>
          Frage <em>{questions.length}</em>
        </span>
        <span>Sprachen</span>
        <span />
      </div>

      <div className="TranslationEditor-rows">
        {questions.map((entry, index) => {
          const issues = faqEntryIssues(entry, languages);
          const isOpen = Boolean(expanded[index]);
          const preview = previewOf(entry, languages);

          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`faq-${index}`}
              className={`TranslationEditor-faqRow ${isOpen ? 'expanded' : ''} ${
                issues.empty || issues.missing.length ? 'incomplete' : ''
              }`}
            >
              <button
                type="button"
                className="TranslationEditor-faqTitle"
                onClick={() => toggle(index)}
                aria-expanded={isOpen}
              >
                <FontAwesomeIcon
                  className="TranslationEditor-caret"
                  icon={isOpen ? faChevronDown : faChevronRight}
                />
                <span className="TranslationEditor-faqNumber">{index + 1}</span>
                <span className="TranslationEditor-faqPreview">
                  {preview || 'Neue Frage – noch kein Text'}
                </span>
              </button>

              <div className="TranslationEditor-cellLanguages">
                {languages.map((code) => (
                  <span
                    key={code}
                    className={`TranslationEditor-langChip ${
                      issues.missing.includes(code) ? '' : 'has'
                    }`}
                    title={
                      issues.missing.includes(code)
                        ? `${code}: Frage oder Antwort fehlt`
                        : `${code}: vollständig`
                    }
                  >
                    {code}
                  </span>
                ))}
              </div>

              <div className="TranslationEditor-faqActions">
                {issues.missing.length > 0 && (
                  <FontAwesomeIcon
                    className="flag broken"
                    icon={faTriangleExclamation}
                    title={`fehlt in ${issues.missing.join(', ')}`}
                  />
                )}
                <button
                  type="button"
                  className="TranslationEditor-iconButton"
                  onClick={() => onMoveEntry(index, index - 1)}
                  disabled={index === 0}
                  aria-label={`Frage ${index + 1} nach oben`}
                  title="nach oben"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="TranslationEditor-iconButton"
                  onClick={() => onMoveEntry(index, index + 1)}
                  disabled={index === questions.length - 1}
                  aria-label={`Frage ${index + 1} nach unten`}
                  title="nach unten"
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="TranslationEditor-iconButton danger"
                  onClick={() => onRemoveEntry(index)}
                  aria-label={`Frage ${index + 1} entfernen`}
                  title="Frage entfernen"
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
              </div>

              {isOpen && (
                <div className="TranslationEditor-faqEditor">
                  {languages.map((code) => (
                    <div
                      key={code}
                      className={`TranslationEditor-langBlock ${
                        issues.missing.includes(code) ? '' : 'current'
                      }`}
                    >
                      <div className="TranslationEditor-langHead">
                        <span className="TranslationEditor-langCode">
                          {code}
                        </span>
                        {issues.missing.includes(code) && (
                          <span className="TranslationEditor-langError">
                            <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                            unvollständig
                          </span>
                        )}
                      </div>

                      <label
                        className="TranslationEditor-faqField"
                        htmlFor={`faq-q-${index}-${code}`}
                      >
                        <span>Frage</span>
                        <input
                          id={`faq-q-${index}-${code}`}
                          type="text"
                          className="TranslationEditor-input"
                          value={entry.question[code] || ''}
                          onChange={(event) =>
                            onChangeField(
                              index,
                              'question',
                              code,
                              event.target.value,
                            )
                          }
                        />
                      </label>

                      <label
                        className="TranslationEditor-faqField"
                        htmlFor={`faq-a-${index}-${code}`}
                      >
                        <span>Antwort ({className})</span>
                        <textarea
                          id={`faq-a-${index}-${code}`}
                          className="TranslationEditor-input"
                          rows={Math.min(
                            10,
                            Math.max(
                              2,
                              String(entry.answer[code] || '').length / 90 + 2,
                            ),
                          )}
                          value={entry.answer[code] || ''}
                          onChange={(event) =>
                            onChangeField(
                              index,
                              'answer',
                              code,
                              event.target.value,
                            )
                          }
                        />
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="TranslationEditor-button"
        onClick={onAddEntry}
      >
        <FontAwesomeIcon icon={faPlus} /> Frage hinzufügen
      </button>
    </div>
  );
};

FaqEditor.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.object.isRequired,
      answer: PropTypes.object.isRequired,
    }),
  ).isRequired,
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
  className: PropTypes.string.isRequired,
  onChangeField: PropTypes.func.isRequired,
  onAddEntry: PropTypes.func.isRequired,
  onRemoveEntry: PropTypes.func.isRequired,
  onMoveEntry: PropTypes.func.isRequired,
};

export default FaqEditor;
